import { useEffect, useState } from 'react';
import axios from 'axios';
import { History, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { STATUS_LABELS, END_REASONS } from '../lib/firmAccess';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const METHOD_LABELS = { POST: 'Oluşturma / İşlem', PUT: 'Güncelleme', PATCH: 'Güncelleme', DELETE: 'Silme' };

const ActionsDialog = ({ session, onClose }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!session) return;
    setRows(null);
    axios.get(`${API}/firm-access/history/${session.id}/actions`).then(r => setRows(r.data)).catch(() => setRows([]));
  }, [session]);
  return (
    <Dialog open={!!session} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="firm-access-actions-dialog">
        <DialogHeader><DialogTitle>İSG {session?.staff_name} — firma adına yapılan işlemler</DialogTitle></DialogHeader>
        <div className="max-h-96 overflow-y-auto divide-y text-sm">
          {rows === null && <p className="p-3 text-slate-400">Yükleniyor...</p>}
          {rows?.length === 0 && <p className="p-3 text-slate-400">Bu erişimde değişiklik yapılmadı (yalnızca görüntüleme).</p>}
          {rows?.map(a => (
            <div key={a.id} className="flex gap-3 py-2">
              <span className="w-32 shrink-0 text-slate-500">{fmt(a.at)}</span>
              <span className="w-36 shrink-0 font-medium">{METHOD_LABELS[a.method] || a.method}</span>
              <span className="truncate font-mono text-xs text-slate-600" title={a.path}>{a.path.replace('/api/', '')}</span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function FirmAccessHistoryPage() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_super_admin;
  const [data, setData] = useState({ items: [], total: 0, total_pages: 0 });
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('all');
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    axios.get(`${API}/firm-access/history`, { params: { page, status } }).then(r => setData(r.data)).catch(() => {});
  }, [page, status]);

  return (
    <div className="space-y-6" data-testid="firm-access-history-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2"><History className="h-7 w-7 text-slate-700" /> Firma Erişim Geçmişi</h1>
          <p className="text-sm text-slate-500 mt-1">{isAdmin ? 'Tüm İSG uzmanlarının firma erişimleri' : 'Firmanıza yapılan İSG erişimleri'}: kim, ne zaman, kaç dakika.</p>
        </div>
        <Select value={status} onValueChange={v => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-48" data-testid="firm-access-history-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm durumlar</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>İSG Uzmanı</TableHead>
                {isAdmin && <TableHead>Firma</TableHead>}
                <TableHead>Talep</TableHead>
                <TableHead>Süre</TableHead>
                <TableHead>Başlangıç</TableHead>
                <TableHead>Bitiş</TableHead>
                <TableHead>Kullanılan</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead className="text-right">İşlem</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400" data-testid="firm-access-history-empty">Kayıt bulunamadı</TableCell></TableRow>
              )}
              {data.items.map(s => {
                const st = STATUS_LABELS[s.status] || {};
                return (
                  <TableRow key={s.id} data-testid={`firm-access-history-row-${s.id}`}>
                    <TableCell className="font-medium">{s.staff_name}</TableCell>
                    {isAdmin && <TableCell>{s.company_name}</TableCell>}
                    <TableCell className="text-sm">{fmt(s.requested_at)}</TableCell>
                    <TableCell className="text-sm">{s.minutes} dk</TableCell>
                    <TableCell className="text-sm">{fmt(s.started_at)}</TableCell>
                    <TableCell className="text-sm">{fmt(s.ended_at)}</TableCell>
                    <TableCell className="text-sm">{s.used_minutes != null ? `${s.used_minutes} dk` : '—'}</TableCell>
                    <TableCell>
                      <span className={`rounded px-2 py-0.5 text-xs font-medium ${st.color}`}>{st.label}</span>
                      {s.end_reason && <span className="block text-[11px] text-slate-500 mt-0.5">{END_REASONS[s.end_reason]}</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      {s.started_at ? (
                        <Button size="sm" variant="outline" onClick={() => setDetail(s)} data-testid={`firm-access-history-actions-${s.id}`}>{s.actions_count || 0} işlem</Button>
                      ) : <span className="text-slate-300">—</span>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {data.total_pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="firm-access-history-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span>{page} / {data.total_pages}</span>
          <Button size="sm" variant="outline" disabled={page >= data.total_pages} onClick={() => setPage(p => p + 1)} data-testid="firm-access-history-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      )}
      <ActionsDialog session={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
